import { motion, useReducedMotion } from 'motion/react';

/*
 * Readiness gauge, drawn as a measuring scale rather than a dashboard dial —
 * ticks every 10, a labelled marker at the score, and the four bands named
 * underneath. Matches the blueprint language used across the site.
 */

const BANDS = [
  { key: 'exploring', from: 0,  to: 32,  label: 'Explore' },
  { key: 'forming',   from: 32, to: 55,  label: 'Map'     },
  { key: 'ready',     from: 55, to: 78,  label: 'Scope'   },
  { key: 'urgent',    from: 78, to: 100, label: 'Build'   },
];

export default function AssessGauge({ score, bandKey, bandLabel, bandNote }) {
  const reduce = useReducedMotion();
  const pct = Math.max(0, Math.min(100, score));

  return (
    <div className="fa-plate fa-ticks p-5 sm:p-6" style={{ background: 'var(--fa-surface)' }}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="fa-ref fa-ref--signal">Readiness</span>
        <span className="fa-ref" style={{ fontSize: 9 }}>0 – 100</span>
      </div>

      {/* Scale */}
      <div className="relative mt-7 mb-2">
        {/* Tick row */}
        <div className="flex justify-between items-end" aria-hidden="true" style={{ height: 9 }}>
          {Array.from({ length: 11 }, (_, i) => (
            <span
              key={i}
              style={{
                width: 1,
                height: i % 5 === 0 ? 9 : 5,
                background: 'var(--fa-muted)',
                opacity: i % 5 === 0 ? 0.7 : 0.35,
                display: 'block',
              }}
            />
          ))}
        </div>

        {/* Track */}
        <div className="relative mt-1" style={{ height: 6, background: 'var(--fa-rule)', borderRadius: 2 }}>
          <motion.div
            initial={reduce ? false : { width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            style={{ height: '100%', background: 'var(--fa-signal)', borderRadius: 2 }}
          />
          {/* Marker */}
          <motion.div
            initial={reduce ? false : { left: '0%', opacity: 0 }}
            animate={{ left: `${pct}%`, opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            className="absolute"
            style={{ top: -5, transform: 'translateX(-50%)' }}
          >
            <span
              style={{
                display: 'block', width: 2, height: 16,
                background: 'var(--fa-ink)', borderRadius: 1,
              }}
            />
          </motion.div>
        </div>

        {/* Band labels */}
        <div className="flex mt-2" aria-hidden="true">
          {BANDS.map((b) => (
            <span
              key={b.key}
              className="fa-ref"
              style={{
                flexBasis: `${b.to - b.from}%`,
                fontSize: 8.5,
                color: b.key === bandKey ? 'var(--fa-signal)' : 'var(--fa-muted)',
                opacity: b.key === bandKey ? 1 : 0.55,
                textAlign: 'center',
              }}
            >
              {b.label}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-5 flex flex-wrap items-baseline gap-x-4 gap-y-1" style={{ borderTop: '1px solid var(--fa-rule)' }}>
        <span
          className="fa-mono font-bold"
          style={{ color: 'var(--fa-signal)', fontSize: 34, lineHeight: 1 }}
        >
          {pct}
        </span>
        <span className="font-heading text-[17px] font-semibold" style={{ color: 'var(--fa-ink)' }}>
          {bandLabel}
        </span>
      </div>
      <p className="mt-3 text-[14px] fa-glow-text fa-glow-text--muted" style={{ maxWidth: '52ch' }}>
        {bandNote}
      </p>
    </div>
  );
}
